import { ReactNode, useState, useCallback } from 'react';
import { AppShell, Box, Drawer } from '@mantine/core';
import { useClickOutside, useMediaQuery } from '@mantine/hooks';
import demoClasses from './DemoLayout.module.css';
import MainHeader from '../components/MainHeader';
import MainSidebar from '../components/MainSidebar';
import MobileBottomTabs from '../components/MobileBottomTabs';
import MainFooter from '../components/MainFooter';
import MainBackground from '../components/MainBackground';
import ScreenLockModal from '../components/ScreenLockModal';
import { LayoutContainer } from '../../components/ui/layout';
import { usePreferencesStore } from '../../stores';
import { usePresenceManager } from '../../hooks/usePresenceManager';
import { PresenceStatus } from '@actschurch/ccms-sdk';

interface DemoLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
}

/**
 * DemoLayout Component
 * Layout orchestrator specifically for Demo pages
 * Provides header, comprehensive demo sidebar with all pages, footer, and content area
 */
export default function DemoLayout({
  children,
  showSidebar = true,
  showHeader = true,
  showFooter = true,
}: DemoLayoutProps) {
  const sidebarCollapsed = usePreferencesStore((state) => state.sidebarCollapsed);
  const toggleSidebar = usePreferencesStore((state) => state.toggleSidebar);
  const setSidebarCollapsed = usePreferencesStore((state) => state.setSidebarCollapsed);
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuAutoHide = usePreferencesStore((state) => state.menuAutoHide);

  const isMobile = useMediaQuery('(max-width: 48em)', undefined, { getInitialValueInEffect: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Mobile: burger / "More" tab opens the full-menu drawer. Desktop: toggle collapse.
  const handleSidebarToggle = useCallback(() => {
    if (isMobile !== false) setMobileMenuOpen(true);
    else toggleSidebar();
  }, [isMobile, toggleSidebar]);

  // Screen lock state - persisted in preferences
  const isLocked = usePreferencesStore((state) => state.screenLocked);
  const setScreenLocked = usePreferencesStore((state) => state.setScreenLocked);
  const setPresenceStatus = usePreferencesStore((state) => state.setPresenceStatus);

  // Presence integration
  const { isConnected, currentUserPresence, updateStatus } = usePresenceManager();

  // Auto-close sidebar when clicking outside (only when auto-hide is enabled and sidebar is open)
  const sidebarRef = useClickOutside(() => {
    if (menuAutoHide && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  });

  const handleLockScreen = async () => {
    // Lock the screen
    setScreenLocked(true);

    // Set presence to Away when locking
    try {
      setPresenceStatus('Away' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[DemoLayout] Presence state:', {
        isConnected: isConnected,
        currentUserPresence: currentUserPresence,
      });

      if (isConnected) {
        console.log('[DemoLayout] Attempting to update presence to Away...');
        await updateStatus('Away' as unknown as PresenceStatus);
        console.log('[DemoLayout] Presence successfully set to Away (screen locked)');
      } else {
        console.warn('[DemoLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[DemoLayout] Failed to update presence on lock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  const handleUnlockScreen = async () => {
    // Unlock the screen
    setScreenLocked(false);

    // Set presence back to Online when unlocking
    try {
      setPresenceStatus('Online' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[DemoLayout] Presence state:', {
        isConnected: isConnected,
        currentUserPresence: currentUserPresence,
      });

      if (isConnected) {
        console.log('[DemoLayout] Attempting to update presence to Online...');
        await updateStatus('Online' as unknown as PresenceStatus);
        console.log('[DemoLayout] Presence successfully set to Online (screen unlocked)');
      } else {
        console.warn('[DemoLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[DemoLayout] Failed to update presence on unlock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  return (
    <>
      {/* Screen Lock Modal - Render first for proper z-index */}
      <ScreenLockModal opened={isLocked} onUnlock={handleUnlockScreen} />

      <MainBackground>
        <AppShell
          header={showHeader ? { height: 60 } : undefined}
          navbar={
            showSidebar
              ? {
                  width: menuIconOnly ? { sm: 72, lg: 72 } : { sm: 200, lg: 250 },
                  breakpoint: 'sm',
                  collapsed: { mobile: true, desktop: sidebarCollapsed },
                }
              : undefined
          }
          footer={showFooter ? { height: 60 } : undefined}
          padding="md"
        >
          {/* Header */}
          {showHeader && (
            <AppShell.Header>
              <MainHeader
                onSidebarToggle={handleSidebarToggle}
                sidebarCollapsed={sidebarCollapsed}
                onLockScreen={handleLockScreen}
                showBurger={showSidebar}
              />
            </AppShell.Header>
          )}

          {/* Sidebar */}
          {showSidebar && (
            <AppShell.Navbar
              ref={sidebarRef}
              className={`${demoClasses.navbar} ${menuIconOnly ? 'icon-rail-nav' : ''}`}
            >
              <MainSidebar />
            </AppShell.Navbar>
          )}

          {/* Main Content */}
          <AppShell.Main>
            <LayoutContainer py="md">{children}</LayoutContainer>
          </AppShell.Main>

          {/* Footer: site footer on tablet/desktop, bottom tabs on mobile */}
          {showFooter && (
            <AppShell.Footer>
              <Box visibleFrom="sm" h="100%">
                <MainFooter />
              </Box>
              <Box hiddenFrom="sm" h="100%">
                <MobileBottomTabs onMore={() => setMobileMenuOpen(true)} />
              </Box>
            </AppShell.Footer>
          )}
        </AppShell>

        {/* Mobile full-menu drawer (auto-closes when a menu item is tapped) */}
        <Drawer
          opened={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          title="Menu"
          position="left"
          size="85%"
          padding="md"
          hiddenFrom="sm"
        >
          <MainSidebar forceExpanded onNavigate={() => setMobileMenuOpen(false)} />
        </Drawer>
      </MainBackground>
    </>
  );
}
